import { create } from "zustand";

export type TicketCartItem = {
  id: number | string;
  quantity: number;
  price: number;
  remaining_stock?: number | null;
  [key: string]: any;
};

type SyncTicketCartResult = {
  adjusted: boolean;
  removed: boolean;
  previousQuantity: number;
  nextQuantity: number;
};

type TicketCartStoreState = {
  items: TicketCartItem[];
  setItemQuantity: (product: TicketCartItem, quantity: number) => void;
  hydrateFromProducts: (products: TicketCartItem[]) => void;
  syncProduct: (product: Partial<TicketCartItem> & { id: number | string }) => SyncTicketCartResult;
  removeItem: (ticketId: number | string) => void;
  clearCart: () => void;
};

const normalizeTicketId = (ticketId: number | string) => String(ticketId);

const clampQuantity = (quantity: number, remainingStock?: number | null) => {
  if (typeof remainingStock !== "number") {
    return quantity;
  }

  return Math.min(quantity, Math.max(remainingStock, 0));
};

const mergeTicketItems = (items: TicketCartItem[]) => {
  const mergedMap = new Map<string, TicketCartItem>();

  items.forEach((item) => {
    const normalizedId = normalizeTicketId(item.id);
    const current = mergedMap.get(normalizedId);
    const requestedQuantity = (current?.quantity || 0) + (item.quantity || 0);
    const remainingStock = item.remaining_stock ?? current?.remaining_stock;
    const nextQuantity = clampQuantity(requestedQuantity, remainingStock);

    if (nextQuantity <= 0) {
      mergedMap.delete(normalizedId);
      return;
    }

    mergedMap.set(normalizedId, {
      ...(current || {}),
      ...item,
      quantity: nextQuantity,
    });
  });

  return Array.from(mergedMap.values());
};

export const useTicketCartStore = create<TicketCartStoreState>((set) => ({
  items: [],

  setItemQuantity: (product, quantity) =>
    set((state) => {
      const normalizedId = normalizeTicketId(product.id);
      const current = state.items.find((item) => normalizeTicketId(item.id) === normalizedId);
      const nextQuantity = clampQuantity(quantity, product.remaining_stock ?? current?.remaining_stock);

      if (nextQuantity <= 0) {
        return {
          items: state.items.filter((item) => normalizeTicketId(item.id) !== normalizedId),
        };
      }

      if (current) {
        return {
          items: state.items.map((item) =>
            normalizeTicketId(item.id) === normalizedId
              ? { ...item, ...product, quantity: nextQuantity }
              : item
          ),
        };
      }

      return {
        items: [...state.items, { ...product, quantity: nextQuantity }],
      };
    }),

  hydrateFromProducts: (products) =>
    set(() => ({
      items: mergeTicketItems(products.filter((item) => (item.quantity || 0) > 0)),
    })),

  syncProduct: (product) => {
    let result: SyncTicketCartResult = {
      adjusted: false,
      removed: false,
      previousQuantity: 0,
      nextQuantity: 0,
    };

    set((state) => {
      const normalizedId = normalizeTicketId(product.id);
      const current = state.items.find((item) => normalizeTicketId(item.id) === normalizedId);

      if (!current) {
        return state;
      }

      const nextQuantity = clampQuantity(current.quantity, product.remaining_stock ?? current.remaining_stock);

      result = {
        adjusted: nextQuantity !== current.quantity,
        removed: nextQuantity <= 0,
        previousQuantity: current.quantity,
        nextQuantity,
      };

      if (nextQuantity <= 0) {
        return {
          items: state.items.filter((item) => normalizeTicketId(item.id) !== normalizedId),
        };
      }

      return {
        items: state.items.map((item) =>
          normalizeTicketId(item.id) === normalizedId
            ? { ...item, ...product, quantity: nextQuantity }
            : item
        ),
      };
    });

    return result;
  },

  removeItem: (ticketId) =>
    set((state) => ({
      items: state.items.filter((item) => normalizeTicketId(item.id) !== normalizeTicketId(ticketId)),
    })),

  clearCart: () => set({ items: [] }),
}));
